import React from 'react';
import { ContextMenuState } from 'src/types/ViewModel/Vessel/TankPlan';
import { useStowageStore } from 'src/context/StowageContext';
import { useStowageSelectionStore } from 'src/context/StowageSelectionContext';
import { hasPermission } from 'src/utils/stowage/permissions/stowagePermissionsUtils';
import { useAuthStore } from 'src/context/AuthContext';
import { StowageRuleConsequenceType } from 'src/enums/stowage/stowageRule/stowageRuleConsequenceType';
import { acceptSoftRuleViolations } from 'src/utils/stowage/violations/violationsUtils';
import { StowageRuleViolationStatus } from 'src/enums/stowage/stowageRule/stowageRuleViolationStatus';
import { stowageFeatureEnum } from 'src/enums/stowage/stowageFeatureEnum';
import usePostStowageApi from 'src/api/stowage/usePostStowageApi';
import { StowagePlanChange } from 'src/types/ViewModel/Stowage/StowagePlanChange';
import { StowageChangeDataType } from 'src/types/ViewModel/Stowage/Enums/StowageChangeDataType';
import { Violation } from 'src/types/ViewModel/Stowage/Violation/Violation';
import { createStowagePlanChange } from 'src/factory/Stowage/StowagePlanChanges/StowagePlanChangeFactory';
import { createViolationChangeData } from 'src/factory/Stowage/StowagePlanChanges/StowageChangeData/ViolationChangeDataFactory';
import OdfjellContextMenu, { MenuItem } from 'src/components/ui/odfjellContextMenu/OdfjellContextMenu';
import { deepCopy } from 'src/utils/objectUtils';

interface ViolationListContextMenuProps {
    contextMenuState: ContextMenuState;
}

const ViolationListContextMenu: React.FC<ViolationListContextMenuProps> = ({ contextMenuState }) => {
    const { activeStowagePlan, setViolationListContextMenuState } = useStowageStore();
    const { selectedViolations, filteredViolations } = useStowageSelectionStore();
    const { role, loggedInUser } = useAuthStore();
    const { saveStowageData } = usePostStowageApi();

    const onAcceptSoftRuleViolation = async () => {
        if (!canAcceptSoftRuleViolation()) {
            setViolationListContextMenuState({ visible: false, cargoContents: [] } as ContextMenuState);
            return;
        }
        const _selectedViolations = deepCopy(selectedViolations);

        acceptSoftRuleViolations(_selectedViolations, loggedInUser.userName);

        const stowagePlanChanges = mapViolationsToStowagePlanChanges(_selectedViolations);
        await saveStowageData(activeStowagePlan, stowagePlanChanges);
        setViolationListContextMenuState({ visible: false, cargoContents: [] } as ContextMenuState);
    };

    const onAcceptAllSoftRuleViolation = () => {
        if (!canAcceptAllSoftRuleViolation()) {
            setViolationListContextMenuState({ visible: false, cargoContents: [] } as ContextMenuState);
            return;
        }

        const acceptedViolations = filteredViolations?.filter(
            (o) => o.type === StowageRuleConsequenceType.SoftRule && o.status === StowageRuleViolationStatus.New
        );
        acceptSoftRuleViolations(acceptedViolations, loggedInUser.userName);

        const stowagePlanChanges = mapViolationsToStowagePlanChanges(acceptedViolations);
        saveStowageData(activeStowagePlan, stowagePlanChanges);
        setViolationListContextMenuState({ visible: false, cargoContents: [] } as ContextMenuState);
    };

    const canAcceptSoftRuleViolation = (): boolean => {
        return (
            activeStowagePlan &&
            selectedViolations?.length === 1 &&
            selectedViolations[0].type === StowageRuleConsequenceType.SoftRule &&
            selectedViolations[0].status === StowageRuleViolationStatus.New &&
            loggedInUser?.userName !== undefined
        );
    };

    const canAcceptAllSoftRuleViolation = (): boolean => {
        return (
            activeStowagePlan &&
            filteredViolations?.filter((o) => o.type === StowageRuleConsequenceType.SoftRule && o.status === StowageRuleViolationStatus.New).length >
                0 &&
            loggedInUser?.userName !== undefined
        );
    };

    const mapViolationsToStowagePlanChanges = (_violations: Violation[]) => {
        const stowagePlanChanges: StowagePlanChange[] = _violations.map((violation) => {
            const changeData = createViolationChangeData(violation);

            return createStowagePlanChange(activeStowagePlan.stowagePlanGuid, violation.violationGuid, StowageChangeDataType.Violation, changeData);
        });

        return stowagePlanChanges;
    };

    const menuItems = [
        {
            content: 'Accept soft rule violation',
            isDisabled: !(hasPermission(role, stowageFeatureEnum.AcceptSoftRuleViolation) && canAcceptSoftRuleViolation()),
            onClick: onAcceptSoftRuleViolation,
        },
        {
            content: 'Accept all soft rule violations',
            isDisabled: !(hasPermission(role, stowageFeatureEnum.AcceptSoftRuleViolation) && canAcceptAllSoftRuleViolation()),
            onClick: onAcceptAllSoftRuleViolation,
        },
    ];

    const createMenuItems = (): MenuItem[] =>
        menuItems.map((item) => ({
            isSeparator: false,
            content: item.content,
            isDisabled: item.isDisabled,
            onClick: item.onClick,
            className: undefined,
        }));

    return (
        <>
            {menuItems.length > 0 && (
                <OdfjellContextMenu
                    position={{ x: contextMenuState.x, y: contextMenuState.y }}
                    menuItems={createMenuItems()}
                    dataContextMenu="violation-list"
                    className="violation-list-context-menu"
                />
            )}
        </>
    );
};

export default ViolationListContextMenu;
